import { useState, useMemo } from 'react'
import { useNflDfsPlayers, useNflDfsRoster, useSaveNflDfsRoster, useSubmitNflDfsRoster, useFantasySettings, useFantasyWeekProjections, useNflDfsLive } from '../../hooks/useLeagues'
import { useAuth } from '../../hooks/useAuth'
import { toast } from '../ui/Toast'
import LoadingSpinner from '../ui/LoadingSpinner'
import PlayerHeadshot from '../ui/PlayerHeadshot'
import InjuryBadge from '../ui/InjuryBadge'
import PlayerDetailModal from './PlayerDetailModal'
import MyRosterHeader from './MyRosterHeader'
import { timeAgo } from '../../lib/time'

const SLOTS = [
  { key: 'QB', label: 'QB', positions: ['QB'] },
  { key: 'RB1', label: 'RB', positions: ['RB'] },
  { key: 'RB2', label: 'RB', positions: ['RB'] },
  { key: 'WR1', label: 'WR', positions: ['WR'] },
  { key: 'WR2', label: 'WR', positions: ['WR'] },
  { key: 'WR3', label: 'WR', positions: ['WR'] },
  { key: 'TE', label: 'TE', positions: ['TE'] },
  { key: 'FLEX', label: 'FLEX', positions: ['RB', 'WR', 'TE'] },
  { key: 'DEF', label: 'DEF', positions: ['DEF'] },
]

const POS_FILTERS = ['All', 'QB', 'RB', 'WR', 'TE', 'DEF', 'OUT']

// Statuses meaning the player cannot take the field this week, so he belongs
// under the OUT tab rather than the main list. Previously only Out and IR
// counted — but "Out" never appears in the data at all, and PUP, Sus and DNR
// were left sitting in the main pool looking pickable. Questionable is
// deliberately absent: that is a real gamble, and it stays in the list with a
// badge.
//
// NA was previously excluded here, on the reasoning that it "looks like an
// unavailable marker and is not (Josh Jacobs carries it)". That was backwards:
// Jacobs is the ONLY player in the league carrying NA, so he was the entire
// sample, and he is on the commissioner's exempt list — ineligible to play.
// Sleeper's NA means not active, which is exactly what this set is for.
// Suspensions match by PREFIX: Sleeper writes 'Sus', 'Suspended' AND
// 'Suspension', and enumerating them is what let the third spelling through.
const UNAVAILABLE_STATUSES = new Set(['out', 'ir', 'pup', 'sus', 'suspended', 'suspension', 'dnr', 'na'])
const isUnavailable = (status) => {
  const s = String(status || '').trim().toLowerCase()
  return s.startsWith('sus') || UNAVAILABLE_STATUSES.has(s)
}

// Copy for an empty player pool.
//
// Salaries are generated overnight but held in draft until Tuesday 10:00 AM
// PT (see the publish gate in scheduler.js), which gives the admin the
// morning to reprice. During that window the pool is legitimately empty, so
// say exactly when it opens rather than leaving users guessing.
//
// Keyed off the pool being empty, not off the clock alone — the moment
// salaries publish, `players` fills and this disappears on its own. If a
// publish is ever late, the note stays up instead of claiming availability
// that isn't there.
function salaryAvailabilityNote(week) {
  const pt = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }))
  if (pt.getDay() === 2 && pt.getHours() < 10) {
    return `Player salaries for Week ${week} become available at 10:00 AM PT today.`
  }
  return `Players and salaries for Week ${week} become available Tuesday at 10:00 AM PT.`
}

export default function NflSalaryCapView({ league }) {
  const { profile } = useAuth()
  const { data: settings } = useFantasySettings(league.id)
  const currentWeek = settings?.current_week || settings?.single_week || 1
  const season = settings?.season || 2026
  const salaryCap = settings?.salary_cap || 60000

  const { data: players, isLoading: playersLoading } = useNflDfsPlayers(currentWeek, season)
  const { data: roster, isLoading: rosterLoading } = useNflDfsRoster(league.id, currentWeek, season)
  const saveRoster = useSaveNflDfsRoster()
  const submitRoster = useSubmitNflDfsRoster()
  // Per-week opponent map — drives the "vs MIA" / "@ MIA" / BYE
  // marker on both rostered slots and the available-players list.
  // Re-uses the same hook traditional fantasy uses.
  const { data: weekContextData } = useFantasyWeekProjections(league.id, currentWeek || null)
  const oppMap = weekContextData?.opponents
  function oppLabel(team) {
    if (!team || !oppMap) return null
    const op = oppMap[team]
    if (!op) return { text: 'BYE', isBye: true }
    // Kickoff rides along with the opponent. starts_at is already in this
    // payload — the roster showed "vs DEN" with no indication of WHEN, which
    // is the thing that decides whether you can still swap the player.
    // Eastern throughout, as everywhere else NFL kickoffs are shown.
    let kickoff = null
    if (op.starts_at) {
      const d = new Date(op.starts_at)
      if (!Number.isNaN(d.getTime())) {
        kickoff = d.toLocaleString('en-US', {
          weekday: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
        })
      }
    }
    return { text: `${op.is_home ? 'vs' : '@'} ${op.opponent}`, isBye: false, kickoff }
  }

  const [posFilter, setPosFilter] = useState('All')
  const [searchQuery, setSearchQuery] = useState('')
  const [detailPlayerId, setDetailPlayerId] = useState(null)
  // Slot key currently being saved, so only THAT row shows a pending state
  // rather than all nine reacting to a shared mutation flag.
  const [pendingSlot, setPendingSlot] = useState(null)
  // When user taps "Edit Roster" after submitting, hide the Submitted
  // badge so the Submit Roster button reappears and they can confirm
  // their changes. Resets on successful resubmit (handleSubmit) so the
  // badge comes back when they commit.
  const [editingAfterSubmit, setEditingAfterSubmit] = useState(false)

  // Edit mode: true when the user hasn't submitted yet OR has explicitly
  // tapped "Edit Roster" after submit. Gates the X (remove) and + (add)
  // affordances so the roster doesn't silently mutate after submit.
  //
  // Declared AFTER editingAfterSubmit, which it reads. This used to sit
  // above that useState and only survived because || short-circuits: while
  // submitted_at was null the left side was true and the right was never
  // evaluated. The moment a roster was submitted, submitted_at became set,
  // JS evaluated editingAfterSubmit inside its temporal dead zone, and the
  // page crashed with "Cannot access 'E' before initialization" — every
  // time, for every user, immediately after submitting.
  const isEditing = !roster?.submitted_at || editingAfterSubmit

  // Build current lineup from saved roster
  const lineup = useMemo(() => {
    const map = {}
    for (const s of SLOTS) map[s.key] = null
    if (roster?.dfs_roster_slots) {
      for (const slot of roster.dfs_roster_slots) {
        map[slot.roster_slot] = {
          player_id: slot.player_id,
          salary: slot.salary,
          is_locked: slot.is_locked,
          points_earned: slot.points_earned,
          ...slot.nfl_players,
        }
      }
    }
    return map
  }, [roster])

  const hasSavedRoster = roster?.dfs_roster_slots?.length > 0
  const usedPlayerIds = useMemo(() => new Set(Object.values(lineup).filter(Boolean).map((p) => p.player_id || p.id)), [lineup])
  const totalSalary = useMemo(() => Object.values(lineup).reduce((sum, p) => sum + (p?.salary || 0), 0), [lineup])
  const remaining = salaryCap - totalSalary
  const filledCount = Object.values(lineup).filter(Boolean).length

  // Live week total, read from the SAME endpoint the Live tab renders rather
  // than summed locally off the roster rows. Those two bases disagree: the
  // roster locks a player on kickoff time, while /dfs/live scores on ESPN
  // game status, so a local sum would drift from the Live tab for any game
  // where the two don't line up. One source, one number.
  const liveQuery = useNflDfsLive(league.id, currentWeek, season)
  const myLive = liveQuery.data?.members?.find((m) => m.user_id === profile?.id)
  const livePoints = myLive?.total_points || 0

  // Filter available players. Mirrors the NBA DFS pattern: by default
  // exclude players marked Out / IR so a user can't accidentally roster
  // an inactive (whose salary still reflects healthy-FPPG pricing); the
  // dedicated OUT filter lets a curious user inspect who's ruled out.
  const available = useMemo(() => {
    if (!players) return []
    return players
      .filter((p) => !usedPlayerIds.has(p.id))
      .filter((p) => {
        if (posFilter === 'OUT') return isUnavailable(p.injury_status)
        if (isUnavailable(p.injury_status)) return false
        return posFilter === 'All' || p.position === posFilter
      })
      .filter((p) => !searchQuery || p.full_name?.toLowerCase().includes(searchQuery.toLowerCase()))
      // Unaffordable players stay out of the browse list but appear when
      // searched by name, disabled — see the NBA view for the reasoning.
      .filter((p) => posFilter === 'OUT' || searchQuery || p.salary <= remaining)
      .sort((a, b) => (b.salary || 0) - (a.salary || 0))
    // No display cap. This used to .slice(0, 100), which only ever bit on the
    // All tab — every position tab filters below 100 on its own. With ~1,100
    // players priced, All showed the top 100 by salary and silently hid
    // everything under ~$5,700, so the value plays you need to fit under the
    // cap were unreachable. Headshots are loading="lazy", so offscreen rows
    // cost no requests.
  }, [players, usedPlayerIds, posFilter, searchQuery, remaining])

  function addPlayer(player) {
    // Find first empty eligible slot
    const slot = SLOTS.find((s) => !lineup[s.key] && s.positions.includes(player.position))
    if (!slot) { toast('No eligible slot available', 'error'); return }
    handleSave(slot.key, player)
  }

  async function handleSave(slotKey, player) {
    const newLineup = { ...lineup, [slotKey]: { ...player, player_id: player.id, salary: player.salary } }
    const slots = SLOTS.map((s) => {
      const p = newLineup[s.key]
      return p ? { roster_slot: s.key, player_id: p.player_id || p.id, salary: p.salary || 0 } : null
    }).filter(Boolean)

    try {
      await saveRoster.mutateAsync({ league_id: league.id, week: currentWeek, season, slots })
      setSearchQuery('')
    } catch (err) {
      toast(err.message || 'Failed to save roster', 'error')
    }
  }

  async function handleSubmit() {
    try {
      await submitRoster.mutateAsync({ league_id: league.id, week: currentWeek, season })
      toast('Roster submitted!', 'success')
      setEditingAfterSubmit(false)
    } catch (err) {
      toast(err.message || 'Failed to submit roster', 'error')
    }
  }

  async function removeSlot(slotKey) {
    // Already saving this slot — ignore the repeat tap rather than firing a
    // second identical save.
    if (pendingSlot === slotKey) return

    const newLineup = { ...lineup, [slotKey]: null }
    const slots = SLOTS.map((s) => {
      const p = newLineup[s.key]
      return p ? { roster_slot: s.key, player_id: p.player_id || p.id, salary: p.salary || 0 } : null
    }).filter(Boolean)

    // The row is NOT optimistically emptied. A remove can legitimately fail
    // (kickoff lock, for one), and a player vanishing then reappearing reads
    // worse than a brief wait. Acknowledge the tap instead, and let the
    // refetch be the source of truth.
    setPendingSlot(slotKey)
    try {
      await saveRoster.mutateAsync({ league_id: league.id, week: currentWeek, season, slots })
    } catch (err) {
      toast(err.message || 'Failed to update roster', 'error')
    } finally {
      setPendingSlot(null)
    }
  }

  if (playersLoading || rosterLoading) return <LoadingSpinner />

  return (
    <div className="lg:grid lg:grid-cols-2 lg:gap-6">
      {/* Left column: roster */}
      <div>
        {/* Salary Bar */}
        <div className="rounded-xl border border-text-primary/20 bg-bg-primary/20 backdrop-blur-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-text-muted uppercase tracking-wider font-semibold">Salary Cap</span>
            <span className="text-xs text-text-primary font-semibold">{filledCount}/{SLOTS.length} slots</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className={`font-display text-2xl ${remaining < 0 ? 'text-incorrect' : 'text-correct'}`}>
              ${remaining.toLocaleString()}
            </span>
            <span className="text-xs text-text-primary">of ${salaryCap.toLocaleString()}</span>
          </div>
          <div className="mt-2 h-1.5 bg-text-primary/10 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${remaining < 0 ? 'bg-incorrect' : 'bg-accent'}`}
              style={{ width: `${Math.min((totalSalary / salaryCap) * 100, 100)}%` }}
            />
          </div>
          {(SLOTS.length - filledCount) > 0 && (
            <div className="mt-2 text-xs text-text-primary text-right">
              ${Math.round(remaining / (SLOTS.length - filledCount)).toLocaleString()} avg per player
            </div>
          )}
        </div>


        {/* My Roster */}
        <div className="rounded-xl border border-text-primary/20 overflow-hidden mb-4">
          <MyRosterHeader livePoints={livePoints} status={myLive?.status} />
          {SLOTS.map((slot) => {
            const player = lineup[slot.key]
            const isLocked = player?.is_locked
            const pointsEarned = player?.points_earned || 0
            return (
              <div
                key={slot.key}
                className="flex items-center gap-3 px-4 py-2.5 border-b border-text-primary/10 last:border-b-0 bg-bg-primary"
              >
                <span className="text-xs font-bold text-accent w-7 shrink-0">{slot.label}</span>
                {player ? (
                  <>
                    {/* Name and headshot open the player modal, matching the
                        pool list below. Scoped to this button rather than the
                        whole row so the remove "×" still just removes. */}
                    <button
                      type="button"
                      onClick={() => setDetailPlayerId(player.id)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                    <PlayerHeadshot name={player.full_name} url={player.headshot_url} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-bold text-text-primary truncate">{player.full_name}</span>
                        <InjuryBadge status={player.injury_status} />
                        {isLocked && (
                          <svg className="w-3 h-3 text-text-muted shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="text-xs text-text-muted">
                        {player.position} · {player.team || 'FA'}
                        {(() => {
                          const opp = oppLabel(player.team)
                          if (!opp) return null
                          return (
                            <>
                              <span className={`ml-2 ${opp.isBye ? 'text-yellow-400 font-semibold' : ''}`}>{opp.text}</span>
                              {opp.kickoff && <span className="ml-2 text-text-muted">{opp.kickoff}</span>}
                            </>
                          )
                        })()}
                      </div>
                    </div>
                    </button>
                    {/* Salary AND points, rather than swapping one for the
                        other at kickoff. The price is what you paid and stays
                        relevant all week — it was disappearing exactly when
                        you'd want to judge the buy against what he returned.
                        Salary steps down to the smaller muted green the pool
                        list uses; points sit right of it in bold.

                        The points slot is always rendered — an em dash before
                        kickoff — so the right edge stays aligned down all nine
                        rows instead of shifting as players lock one by one. */}
                    <span className="text-xs font-semibold text-correct/80 tabular-nums shrink-0">
                      ${(player.salary || 0).toLocaleString()}
                    </span>
                    <span className="text-sm font-display text-text-primary tabular-nums w-11 text-right shrink-0">
                      {isLocked ? Math.round(pointsEarned * 10) / 10 : '\u2013'}
                    </span>
                    {!isLocked && isEditing && (() => {
                      const isRemoving = pendingSlot === slot.key
                      return (
                        <button
                          onClick={() => removeSlot(slot.key)}
                          disabled={isRemoving}
                          aria-busy={isRemoving || undefined}
                          aria-label={isRemoving ? `Removing ${player.full_name}` : `Remove ${player.full_name}`}
                          className={`transition-colors text-lg leading-none ${
                            isRemoving
                              ? 'text-text-muted cursor-wait'
                              : 'text-text-muted hover:text-incorrect'
                          }`}
                        >
                          {isRemoving ? (
                            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                          ) : '\u00d7'}
                        </button>
                      )
                    })()}
                  </>
                ) : (
                  <div className="flex-1 text-xs text-text-muted italic">Empty</div>
                )}
              </div>
            )
          })}
        </div>
        {/* Action button — matches NBA DFS pattern: subtle Edit Roster
            when the roster is submitted, accent Submit Roster when
            building / changing picks. Auto-save runs on every pick so
            the explicit submit is just the "I commit" moment. */}
        {(() => {
          const isViewMode = roster?.submitted_at && !editingAfterSubmit
          if (isViewMode) {
            return (
              <button
                onClick={() => setEditingAfterSubmit(true)}
                className="w-full mt-3 py-3 rounded-xl font-display bg-bg-card text-text-primary border border-text-primary/20 hover:bg-text-primary/10 transition-colors"
              >
                Edit Roster
              </button>
            )
          }
          if (filledCount === SLOTS.length && remaining >= 0) {
            return (
              <button
                onClick={handleSubmit}
                disabled={submitRoster.isPending}
                className="w-full mt-3 py-3 rounded-xl font-display bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
              >
                {submitRoster.isPending ? 'Submitting...' : roster?.submitted_at ? 'Resubmit Roster' : 'Submit Roster'}
              </button>
            )
          }
          // An incomplete lineup used to render NOTHING here — no button, no
          // reason, no indication the week was not entered. Saves happen
          // automatically, so a 7/9 roster looked and felt finished.
          //
          // That is how a lineup with FLEX and DEF left empty got played:
          // skipping slots frees ~$18k for stars, and nothing on screen said
          // it would not count. From week 9 an unsubmitted roster does not
          // score, so the silence had to go.
          return (
            <div className="w-full mt-3 py-3 px-4 rounded-xl border border-yellow-500/30 bg-yellow-500/[0.06] text-center">
              <div className="text-sm font-semibold text-yellow-500">
                {remaining < 0
                  ? `Over the cap by $${Math.abs(remaining).toLocaleString()}`
                  : `Lineup incomplete \u2014 ${filledCount}/${SLOTS.length} slots filled`}
              </div>
              <div className="text-xs text-text-primary/70 mt-1">
                {remaining < 0
                  ? 'Drop a player or swap in someone cheaper, then submit.'
                  : 'Fill every slot and hit Submit Roster. An unsubmitted lineup does not count for the week.'}
              </div>
            </div>
          )
        })()}
      </div>

      {/* Right column: player pool */}
      <div className="rounded-xl border border-text-primary/20 overflow-hidden lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto lg:sticky lg:top-4">
        {/* Pinned so search + position filters stay reachable while scrolling
            the pool. On desktop the list is max-h-none and the PANEL scrolls,
            which used to carry these controls off-screen and force a scroll
            back up to switch position. On mobile the list scrolls inside its
            own max-h box, so the header already stayed put — sticky is inert
            there. Tinted rather than opaque so the backdrop reads through,
            matching the Salary Cap panel — but heavier than that panel's /20
            with a stronger blur, because rows scroll UNDER this one and at
            /20 they'd stay legible right through the search box. */}
        <div className="sticky top-0 z-10 bg-bg-primary/70 backdrop-blur-md px-4 py-3 border-b border-text-primary/10">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Available Players</h3>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search players..."
            className="w-full bg-bg-primary border border-text-primary/20 rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent mb-3"
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex gap-1.5 flex-wrap">
              {POS_FILTERS.map((pos) => (
                <button
                  key={pos}
                  onClick={() => setPosFilter(pos)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                    pos === 'OUT'
                      ? posFilter === pos
                        ? 'bg-incorrect/20 text-incorrect border border-incorrect/40'
                        : 'border border-incorrect/30 text-incorrect/70 hover:bg-incorrect/10'
                      : posFilter === pos
                        ? 'bg-accent text-white'
                        : 'border border-text-primary/20 text-text-primary hover:bg-text-primary/10'
                  }`}
                >
                  {pos === 'OUT' ? 'O' : pos}
                </button>
              ))}
            </div>
            {(SLOTS.length - filledCount) > 0 && (
              <span
                key={`${remaining}-${filledCount}`}
                className="text-xs text-text-primary shrink-0"
              >
                ${Math.round(remaining / (SLOTS.length - filledCount)).toLocaleString()} avg/player
              </span>
            )}
          </div>
        </div>

        {!available.length ? (
          <div className="px-4 py-6 text-center text-xs text-text-muted">
            {!players?.length
              ? salaryAvailabilityNote(currentWeek)
              : filledCount >= SLOTS.length
                // Every slot filled: the pool is empty because the last
                // filter is `salary <= remaining` and remaining is $0, not
                // because nothing matched. "No players match your filters"
                // sent me looking for a filter bug that wasn't there.
                ? 'Your roster is full. Drop a player to see available replacements.'
                : remaining <= 0
                  ? 'No cap space remaining. Drop a player to free up salary.'
                  : 'No players match your filters.'}
          </div>
        ) : (
          <div className="max-h-[50vh] lg:max-h-none overflow-y-auto">
            {available.map((player) => (
              <div
                key={player.id}
                className="flex items-center gap-3 px-4 py-2.5 border-b border-text-primary/10 last:border-b-0 bg-bg-primary"
              >
                <button
                  type="button"
                  onClick={() => setDetailPlayerId(player.id)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left hover:bg-text-primary/5 -mx-1 px-1 py-1 rounded-lg transition-colors"
                >
                  <PlayerHeadshot name={player.full_name} url={player.headshot_url} size="md" />
                  <div className="flex-1 min-w-0">
                    {/* Questionable players are a legitimate pick and stay in
                        the pool, so the flag is the only thing distinguishing
                        them from healthy ones. Without it 132 of them looked
                        identical on the 2026 week 1 slate. */}
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="text-sm font-bold text-text-primary truncate">{player.full_name}</span>
                      <InjuryBadge status={player.injury_status} />
                    </span>
                    <div className="text-xs text-text-muted">
                      {player.position} · {player.team || 'FA'}
                      {(() => {
                        const opp = oppLabel(player.team)
                        if (!opp) return null
                        return (
                          <>
                            <span className={`ml-2 ${opp.isBye ? 'text-yellow-400 font-semibold' : ''}`}>{opp.text}</span>
                            {opp.kickoff && <span className="ml-2 text-text-muted">{opp.kickoff}</span>}
                          </>
                        )
                      })()}
                    </div>
                  </div>
                  <span className="text-base font-semibold text-accent tabular-nums shrink-0">${(player.salary || 0).toLocaleString()}</span>
                </button>
                {/* A locked player stays listed but is visibly unavailable.
                    The server has always rejected adding one; the pool just
                    had no way to say so, so a player mid-game looked addable
                    and tapping + simply failed. */}
                {isEditing && (() => {
                  // Too expensive is now a second reason a row can be present
                  // but unaddable. Those rows only appear via search — the
                  // browse list still filters them — so reaching one means the
                  // user asked for that player by name and deserves the price
                  // rather than an empty result.
                  const tooPricey = player.salary > remaining
                  const blocked = player.is_locked || tooPricey
                  return (
                    <button
                      onClick={() => !blocked && addPlayer(player)}
                      disabled={blocked}
                      title={
                        player.is_locked ? 'Game already started'
                          : tooPricey ? `$${player.salary.toLocaleString()} — you have $${Math.max(0, remaining).toLocaleString()} left`
                            : undefined
                      }
                      className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 text-lg font-bold leading-none transition-colors ${
                        blocked
                          ? 'border-text-muted/30 text-text-muted cursor-not-allowed'
                          : 'border-accent/40 text-accent hover:bg-accent hover:text-white'
                      }`}
                    >
                      +
                    </button>
                  )
                })()}
              </div>
            ))}
          </div>
        )}
      </div>
      {detailPlayerId && (
        <PlayerDetailModal leagueId={league.id} playerId={detailPlayerId} onClose={() => setDetailPlayerId(null)} />
      )}
    </div>
  )
}
